import { useEffect, useState } from 'react';
import { AiOutlineEye, AiOutlineEyeInvisible } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { authRegister, authLogin, authVerify, authCheckVerify, setAuthToken } from '../services/api';
import { isValidMobile, sanitizeMobileInput } from '../utils/mobileValidation';

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('login'); // 'login' | 'register'
  const [step, setStep] = useState('form'); // 'form' | 'code'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mobile, setMobile] = useState('');
  const [countryCode, setCountryCode] = useState(localStorage.getItem('countryCode') || '+91');
  const [code, setCode] = useState('');
  const [verificationMobile, setVerificationMobile] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const isRegister = mode === 'register';

  useEffect(() => {
    if (localStorage.getItem('jwt')) {
      navigate('/', { replace: true });
    }
  }, [navigate]);

  const switchMode = (next) => {
    setMode(next);
    setStep('form');
    setStatus('');
    setCode('');
    setVerificationMobile('');
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setStatus('Signing in…');
      // persist selected country code
      try { localStorage.setItem('countryCode', countryCode); } catch (e) { }

      const baseMobile = sanitizeMobileInput(mobile);
      const normalizedMobile = isRegister ? (baseMobile.startsWith('+') ? baseMobile : `${countryCode}${baseMobile}`) : baseMobile;

      if (isRegister && !baseMobile) {
        setStatus('Mobile number is required.');
        setLoading(false);
        return;
      }

      if (isRegister && !isValidMobile(normalizedMobile)) {
        setStatus('Mobile number must contain only digits and optional +, up to 15 characters.');
        setLoading(false);
        return;
      }

      if (mode === 'register') {
        const res = await authRegister({ email, password, mobile: normalizedMobile, channel: 'whatsapp' });

        if (res && res.token) {
          setAuthToken(res.token);
          setStatus('Account created and signed in.');
          navigate('/', { replace: true });
          return;
        }

        if (res && (res.message === 'code_sent' || res.userId)) {
          setVerificationMobile(res.mobile || normalizedMobile);
          try { localStorage.setItem('pendingRegistrationEmail', email || ''); } catch (e) { }
          setStatus('A 6-digit code has been sent to your WhatsApp number.');
          setStep('code');
          setCode('');
          return;
        }

        if (res && res.message) {
          setStatus(res.message === 'registered' ? 'Registered — please log in' : res.message);
          if (res.message === 'registered') {
            setMode('login');
            setStep('form');
          }
          return;
        }
      } else {
        const res = await authLogin({ email, password, channel: 'whatsapp' });
        if (res && res.token) {
          setAuthToken(res.token);
          setStatus('Signed in');
          navigate('/', { replace: true });
          return;
        }

        if (res && res.message === 'code_sent') {
          setVerificationMobile(res.mobile || '');
          setStatus('A 6-digit code has been sent to your WhatsApp number.');
          setStep('code');
          setCode('');
          return;
        }

        setStatus(res?.message || 'Code sent');
        if (!res?.token) setStep('code');
      }
    } catch (err) {
      setStatus(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setStatus('Verifying…');

      let res;
      if (isRegister || verificationMobile) {
        // include the email to ensure the server issues the token for the intended account
        const payload = { mobile: verificationMobile || normalizedMobileFromInput(), code };
        const pendingEmail = email || localStorage.getItem('pendingRegistrationEmail') || '';
        if (pendingEmail) payload.email = pendingEmail;
        res = await authCheckVerify(payload);
      } else {
        res = await authVerify({ email, code });
      }

      if (res && res.token) {
        setAuthToken(res.token);
        setStatus('Signed in');
        try { localStorage.removeItem('pendingRegistrationEmail'); } catch (e) { }
        navigate('/', { replace: true });
        return;
      }

      if (res && res.verified) {
        setAuthToken(res.token);
        setStatus('WhatsApp verified successfully.');
        navigate('/', { replace: true });
        return;
      }

      setStatus(res?.message || 'Verification failed');
    } catch (err) {
      setStatus(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const normalizedMobileFromInput = () => {
    const baseMobile = sanitizeMobileInput(mobile);
    if (!baseMobile) return '';
    return baseMobile.startsWith('+') ? baseMobile : `${countryCode}${baseMobile}`;
  };

  return (
    <div className="auth-shell">
      <div className="card auth-card">
        {/* Header */}
        <div className="auth-step-badge">STEP 0 · SECURE SIGN-IN</div>

        {/* <h2 className="auth-title">
          {step === 'code'
            ? 'Enter SMS code'
            : isRegister
              ? 'Create your account'
              : 'Welcome back'}
        </h2>
        <p className="auth-subtitle">
          {step === 'code'
            ? `We sent a 6-digit code to ${email || 'your phone'}.`
            : isRegister
              ? 'Register to start scoping a BOT engagement.'
              : 'Log in to continue.'}
        </p> */}

        {/* Mode toggle – only on form step */}
        {step === 'form' && (
          <div className="auth-mode-toggle">
            <button
              type="button"
              className={`auth-mode-btn ${isRegister ? 'active' : ''}`}
              onClick={() => switchMode('register')}
            >
              Register
            </button>
            <button
              type="button"
              className={`auth-mode-btn ${!isRegister ? 'active' : ''}`}
              onClick={() => switchMode('login')}
            >
              Log in
            </button>
          </div>
        )}

        {/* Form step */}
        {step === 'form' && (
          <form onSubmit={submit} className="auth-form">
            <div className="form-group">
              <label>Work email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label>Password</label>

              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={isRegister ? "Create a strong password" : "Your password"}
                  required
                  autoComplete={isRegister ? "new-password" : "current-password"}
                />

                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
                >
                  {showPassword ? <AiOutlineEyeInvisible size={20} /> : <AiOutlineEye size={20} />}
                </button>
              </div>
            </div>

            {isRegister && (
              <div className="form-group">
                <label>Mobile phone number  (for whatsapp 2FA)</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <select style={{ width: 'auto' }} value={countryCode} onChange={(e) => setCountryCode(e.target.value)}>
                    <option value="+91">+91</option>
                    <option value="+49">+49</option>
                    <option value="+41">+41</option>
                    <option value="+44">+44</option>
                    <option value="+1">+1</option>
                  </select> 
                  <input
                    type="tel"
                    value={mobile}
                    onChange={(e) => {
                      const nextValue = sanitizeMobileInput(e.target.value);
                      if (nextValue.length <= 12) setMobile(nextValue);
                    }}
                    placeholder="9876543210"
                    maxLength={12}
                    required
                    style={{ flex: 1 }}
                  />
                </div>
              </div>
            )}

            {/* Why 2FA box */}
            <div className="auth-2fa-box">
              <span className="auth-2fa-icon">🔒</span>
              <p>
                <strong>Why 2FA?</strong> The details you enter here — your team plan, cost
                and rate assumptions, and delivery strategy — can be highly sensitive and
                should stay with you only. That is why access is protected by two-factor
                authentication: your password plus a one-time code sent by whatsapp to your phone.
              </p>
            </div>

            {!isRegister && (
              <button
                type="button"
                className="auth-back-link"
                onClick={() => navigate('/forgot-password')}
                style={{ marginTop: '12px', marginBottom: '8px' }}
              >
                Forgot password?
              </button>
            )}

            <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
              {isRegister ? 'Create account & send WhatsApp code →' : 'Log in →'}
            </button>
          </form>
        )}

        {/* Code verification step */}
        {step === 'code' && (
          <form onSubmit={verify} className="auth-form">
            <div className="form-group">
              <label>Enter the 6-digit code sent to {verificationMobile || 'your WhatsApp number'}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                required
                autoFocus
              />
            </div>

            <button className="btn btn-primary auth-submit" type="submit" disabled={loading}>
              Verify code →
            </button>

            <button
              type="button"
              className="auth-back-link"
              onClick={() => {
                setStep('form');
                setCode('');
                setVerificationMobile('');
                setStatus('');
              }}
            >
              ← Back to {isRegister ? 'register' : 'log in'}
            </button>
          </form>
        )}

        {status && <div className={` status ${status.includes('fail') || status.includes('Error') ? 'error' : ''}`}>{status}</div>}

        <p className="auth-footer">
          By continuing you agree to keep your engagement data confidential.
        </p>
      </div>
    </div>
  );
}

export default AuthPage;